import React from "react";
import { Link } from "gatsby";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import TrustpilotInline from "../components/TrustpilotInline";
import { COUPLE_TIER_ONE } from "../constants/pricing";
import logoPrimary from "../images/logos/logo_primary.svg";

import "../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const supportAreas = [
	[
		"01",
		"Two personal care plans",
		"Each person is assessed as an individual, with their own routines, preferences, medication, risks and goals reflected clearly in the shared arrangement.",
	],
	[
		"02",
		"Shared daily routines",
		"Meals, appointments and household life can stay joined up while personal support is provided privately and at the right pace for each partner.",
	],
	[
		"03",
		"Medication support",
		"Separate medication records and routines help keep prompts or safe administration clear, accurate and aligned with each person's care plan.",
	],
	[
		"04",
		"Mobility and personal care",
		"Support with washing, dressing, continence, walking and transfers can differ for each person without disrupting life together.",
	],
	[
		"05",
		"Household and companionship",
		"Cooking, shopping, laundry and light housekeeping are shared once, leaving the couple with more time and energy for each other.",
	],
	[
		"06",
		"Family reassurance",
		"One managed arrangement gives relatives a clearer view of how both people are doing and one care team to contact when circumstances change.",
	],
];

const sharedCarePrinciples = [
	"One shared home and familiar way of life",
	"An individual assessment for each person",
	"Private support delivered with dignity",
	"Shared routines where the couple wants them",
	"Clear records for medication and care tasks",
	"A plan that can change for either partner",
];

const situations = [
	[
		"Both partners need everyday support",
		"One professional carer may be able to support both people with personal care, medication, meals, mobility and household routines. The assessment confirms whether the combined needs can be met safely and sustainably.",
	],
	[
		"One partner has become the main carer",
		"Live-in care can lift practical and personal-care responsibility from a spouse or partner. They can remain closely involved without carrying the full physical and emotional load alone.",
	],
	[
		"Each person has different needs",
		"Couples do not need matching diagnoses or support levels. One person may need complex personal care while the other needs lighter assistance, reassurance or help around the home.",
	],
	[
		"Needs change at different times",
		"The Care Manager can review one person's plan without losing sight of how that change affects their partner, the carer and the wider household.",
	],
];

const comparisonRows = [
	[
		"How the cost is quoted",
		`One shared weekly arrangement for the household, from ${COUPLE_TIER_ONE}, confirmed after both assessments.`,
		"Care homes generally assess and quote for each resident. A couple should combine both individual fees, which may differ by room and care needs.",
	],
	[
		"Living together",
		"The couple remains together in their own home and can continue sharing their bedroom if they choose.",
		"Both people need a suitable placement. A shared room, double room or adjacent rooms may not be available and policies vary by home.",
	],
	[
		"Care planning",
		"Each partner has an individual care plan, coordinated through one professional carer and one Edyn care team.",
		"Each person is a separate resident with an individual care plan delivered by the home's wider staff team.",
	],
	[
		"Daily routines",
		"Meals, bedtimes, visitors, pets and outings can continue around the couple's preferences and home life.",
		"Couples can still spend time together, but routines, meals and activities operate within the home's environment and staffing.",
	],
	[
		"One-to-one attention",
		"The live-in carer's working time is dedicated to the couple's household and agreed care plans.",
		"Staff support several residents. The amount of individual time depends on the home, staffing and each resident's assessed needs.",
	],
	[
		"Changing or night-time needs",
		"If combined needs become too high for one carer, Edyn may recommend waking-night or additional support and explain the added cost.",
		"A residential or nursing home may support higher needs, but the appropriate home, room and fee must be confirmed for each person.",
	],
	[
		"What the price includes",
		"Managed live-in care and coordination. The household continues to provide accommodation, food or an allowance, and normal home costs.",
		"Accommodation, meals and care are usually covered by the fee, but nursing, top-ups and other extras vary and should be checked in writing.",
	],
];

const homePrinciples = [
	[
		"Protect private space",
		"Agree which parts of the day and home remain just for the couple, and how personal care will be delivered discreetly.",
	],
	[
		"Plan shared and separate routines",
		"Keep the meals, outings and rituals the couple enjoys together while allowing different rest times, appointments and support needs.",
	],
	[
		"Give the carer a suitable room",
		"A professional carer needs a private bedroom and proper breaks so the arrangement remains safe and sustainable.",
	],
	[
		"Review the whole household",
		"When one person's needs change, the Care Manager considers the impact on both partners and updates the plan accordingly.",
	],
];

const faqs = [
	[
		"Can one live-in carer look after two people?",
		"Often, yes. It depends on each person's needs, the amount of support required during the day and night, the home environment and whether one carer can meet both care plans safely. Edyn confirms this through assessment.",
	],
	[
		"Do both partners receive their own care assessment?",
		"Yes. Each person is assessed individually and has care needs, preferences, risks and outcomes recorded clearly. The two plans are then coordinated around shared household life.",
	],
	[
		"What if one person needs much more care than the other?",
		"That is common. The care plan can give one partner more hands-on support while the other receives lighter assistance. The assessment also checks that the combined workload is realistic for one live-in carer.",
	],
	[
		"Can couples remain in the same bedroom?",
		"Yes, if that is their choice and it remains safe. Equipment, night-time needs and moving-and-handling guidance may affect the practical setup, which the Care Manager will discuss sensitively.",
	],
	[
		"Is couples care cheaper than arranging two separate services?",
		"A shared live-in arrangement can be more cost-effective than two separate placements, but the price depends on the combined level and complexity of care. Edyn confirms the weekly cost after assessment.",
	],
	[
		"What if one partner goes into hospital?",
		"The care team will review what support the person at home still needs and plan for discharge when appropriate. Any change to the arrangement or price will be explained clearly.",
	],
];

export default function CouplesCare() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Live-in Care for Couples at Home | Edyn Care"
				description="Live-in care for couples who want to remain together at home, with two individual care plans coordinated through one fully managed Edyn service."
				path="/couples-care/"
				image="https://www.edyn.care/assets/marketing-site/images/livein-imageclus-05.jpg"
			/>
			<Header />

			<main id="main-content" className="edyn-dementia edyn-couples">
				<section className="dementia-hero">
					<div className="site-shell">
						<div className="dementia-hero__panel">
							<div className="dementia-hero__copy">
								<p className="eyebrow eyebrow--light">
									Live-in care for couples
								</p>
								<h1>Keep caring for each other. Let us carry the rest.</h1>
							</div>
							<div className="dementia-hero__summary">
								<p className="dementia-hero__lead">
									One shared care arrangement can help a couple remain together
									at home, with individual support for each person.
								</p>
								<ul>
									<li>Two personal care plans</li>
									<li>One professional carer in the home</li>
									<li>A fully managed Edyn care team</li>
								</ul>
								<div className="button-row">
									<Link className="button button--cream" to="/care-consultation/">
										Talk to a Care Advisor
									</Link>
									<a
										className="button button--text-light"
										href="#couples-support"
									>
										Explore couples care <span aria-hidden="true">&#8595;</span>
									</a>
								</div>
							</div>
						</div>

						<div className="dementia-hero__visual couples-hero__visual">
							<img
								src={`${imagePath}/livein-imageclus-05.jpg`}
								alt="A couple discussing care plans with an adviser at home"
								width="301"
								height="461"
							/>
							<div className="dementia-hero__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>One home. Two people.</strong>
									Care that respects both.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="condition-trustpilot" aria-label="Customer reviews">
					<div className="site-shell condition-trustpilot__inner">
						<TrustpilotInline />
					</div>
				</section>

				<section
					className="dementia-assurance"
					aria-label="Couples care assurances"
				>
					<div className="site-shell dementia-assurance__grid">
						<p>
							<strong>One shared home</strong>
							<span>life stays familiar</span>
						</p>
						<p>
							<strong>Two care plans</strong>
							<span>individual needs stay clear</span>
						</p>
						<p>
							<strong>Fully managed</strong>
							<span>by one Edyn care team</span>
						</p>
					</div>
				</section>

				<section className="dementia-person section-space">
					<div className="site-shell dementia-person__grid">
						<p className="eyebrow">The relationship comes first</p>
						<div>
							<h2>Care should support life together, not take it over.</h2>
							<div className="dementia-person__copy">
								<p>
									When one or both partners need help, couples can face an
									impossible-feeling choice: struggle on alone or risk being
									separated by different care arrangements.
								</p>
								<p>
									Live-in couples care creates another option. A professional
									carer handles agreed practical and personal support while the
									couple keeps their home, routines and relationship at the
									centre of daily life.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					id="couples-support"
					className="dementia-support section-space section-dark"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow eyebrow--light">Everyday support</p>
								<h2>Care for two people, without treating them as one.</h2>
							</div>
							<p>
								A shared arrangement brings routines together where helpful,
								while preserving individual choices, records and privacy.
							</p>
						</div>
						<div className="dementia-support__grid">
							{supportAreas.map(([number, title, copy]) => (
								<article key={title}>
									<span>{number}</span>
									<h3>{title}</h3>
									<p>{copy}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-understanding section-space">
					<div className="site-shell dementia-understanding__grid">
						<div className="dementia-understanding__intro">
							<p className="eyebrow">How shared care works</p>
							<h2>
								Joined-up support begins with two individual conversations.
							</h2>
							<p>
								We learn what matters to each partner separately and together,
								then build an arrangement that is clear for the couple, their
								family and the professional carer.
							</p>
							<Link className="dementia-source" to="/approach/">
								See how Edyn arranges care{" "}
								<span aria-hidden="true">&#8594;</span>
							</Link>
						</div>
						<ul className="dementia-understanding__signs">
							{sharedCarePrinciples.map((principle, index) => (
								<li key={principle}>
									<span>{String(index + 1).padStart(2, "0")}</span>
									{principle}
								</li>
							))}
						</ul>
					</div>
				</section>

				<section className="dementia-facts section-space">
					<div className="site-shell dementia-facts__panel">
						<div className="dementia-facts__title">
							<img
								src={`${imagePath}/we-care-image-2.jpg`}
								alt=""
								loading="lazy"
							/>
							<div>
								<p className="eyebrow eyebrow--light">The practical picture</p>
								<h2>One arrangement, carefully balanced</h2>
							</div>
						</div>
						<div className="dementia-facts__grid">
							<p>
								<strong>Two assessments</strong>
								<span>because each person is different</span>
							</p>
							<p>
								<strong>One care team</strong>
								<span>coordinating the whole arrangement</span>
							</p>
							<p>
								<strong>Shared routines</strong>
								<span>where they make life easier</span>
							</p>
							<p>
								<strong>Individual records</strong>
								<span>for clear and accountable care</span>
							</p>
							<p>
								<strong>From {COUPLE_TIER_ONE}</strong>
								<span>per week, confirmed after assessment</span>
							</p>
							<p>
								<strong>One familiar home</strong>
								<span>with space to remain a couple</span>
							</p>
						</div>
					</div>
				</section>

				<section
					id="couples-comparison"
					className="livein-compare couples-compare section-space section-clay"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Comparing shared care</p>
								<h2>Couples live-in care compared with a care home.</h2>
							</div>
							<p>
								The biggest difference is not only where care happens. It is
								also whether support is arranged around one shared household or
								two individual residential placements.
							</p>
						</div>
						<div
							className="livein-compare__scroll"
							tabIndex="0"
							aria-label="Scrollable couples care comparison"
						>
							<table className="livein-compare__table">
								<caption className="visually-hidden">
									Comparison of Edyn couples live-in care and care homes
								</caption>
								<thead>
									<tr>
										<th scope="col">What to compare</th>
										<th className="is-edyn" scope="col">
											<span className="couples-compare__brand">
												<img src={logoPrimary} alt="Edyn Care" />
											</span>
										</th>
										<th scope="col">Care home</th>
									</tr>
								</thead>
								<tbody>
									{comparisonRows.map(([label, couplesCare, careHome]) => (
										<tr key={label}>
											<th scope="row">{label}</th>
											<td className="is-edyn">{couplesCare}</td>
											<td>{careHome}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						<div className="couples-compare__notes">
							<p>
								Care homes differ by location, room type, facilities and level
								of care. Ask each provider for two written quotes and confirm
								whether shared or adjacent rooms, nursing care and any top-up
								fees are included.
							</p>
							<a
								className="text-link"
								href="https://www.nhs.uk/social-care-and-support/care-services-equipment-and-care-homes/care-homes/"
								target="_blank"
								rel="noopener noreferrer"
							>
								Read the NHS care home guide{" "}
								<span aria-hidden="true">&#8599;</span>
							</a>
						</div>
					</div>
				</section>

				<section className="dementia-types section-space section-soft">
					<div className="site-shell dementia-types__grid">
						<div>
							<p className="eyebrow">When couples care can help</p>
							<h2>Shared care does not require shared needs.</h2>
							<p>
								The assessment looks at the combined picture and whether one
								professional carer can support both people safely.
							</p>
						</div>
						<div className="dementia-types__list">
							{situations.map(([title, copy], index) => (
								<details key={title} open={index === 0}>
									<summary>
										<span>{String(index + 1).padStart(2, "0")}</span>
										{title}
									</summary>
									<p>{copy}</p>
								</details>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-home section-space">
					<div className="site-shell dementia-home__grid">
						<div className="dementia-home__visual couples-home__visual">
							<img
								src={`${imagePath}/livein-imageclus-02.jpg`}
								alt="Family photographs in a familiar home"
								loading="lazy"
							/>
						</div>
						<div className="dementia-home__copy">
							<p className="eyebrow">Preparing the home</p>
							<h2>Make room for care without losing the feeling of home.</h2>
							<p>
								The household needs to work for two care plans, the couple's
								privacy and the professional carer's working and living needs.
							</p>
							<div className="dementia-home__principles">
								{homePrinciples.map(([title, copy]) => (
									<article key={title}>
										<h3>{title}</h3>
										<p>{copy}</p>
									</article>
								))}
							</div>
						</div>
					</div>
				</section>

				<section className="dementia-managed section-space section-dark">
					<div className="site-shell">
						<div className="dementia-managed__heading">
							<p className="eyebrow eyebrow--light">
								Fully managed couples care
							</p>
							<h2>One carer in the home. A wider team balancing both plans.</h2>
						</div>
						<div className="dementia-managed__steps">
							<article>
								<span>01</span>
								<h3>Assess both</h3>
								<p>
									A Care Manager understands each person's needs and how the
									couple wants daily life to work together.
								</p>
							</article>
							<article>
								<span>02</span>
								<h3>Match carefully</h3>
								<p>
									We select a professional carer with the skills, confidence and
									personality to support the whole household.
								</p>
							</article>
							<article>
								<span>03</span>
								<h3>Review together</h3>
								<p>
									The care team reviews changes for either partner and keeps the
									combined arrangement safe and sustainable.
								</p>
							</article>
						</div>
						<Link className="button button--cream" to="/care-consultation/">
							Discuss care for two
						</Link>
					</div>
				</section>

				<section className="dementia-faq section-space">
					<div className="site-shell dementia-faq__grid">
						<div>
							<p className="eyebrow">Questions couples and families ask</p>
							<h2>Clear answers before making a shared decision.</h2>
							<p>
								Every couple, home and combination of needs is different. An
								assessment turns the general answers here into a specific plan.
							</p>
						</div>
						<div className="dementia-faq__list">
							{faqs.map(([question, answer]) => (
								<details key={question}>
									<summary>{question}</summary>
									<p>{answer}</p>
								</details>
							))}
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
